import UncontrolledPopover from 'components/SafeUncontrolledPopover';
import React, { FC, PropsWithChildren } from 'react';

import { FormattedMessage } from 'react-intl';
import { ShouldShowAIAssistedQuestionDescriptorType } from 'types';
import {
  DEFAULT_OPEN_RESPONSE_QUESTIONS,
  getAIassistedQuestionsVisibilityFieldForCustomQuestionGroupName,
} from 'utils/models/Performance';
import { ifUndefined } from 'utils/util/customOperators';
import { useCampaignEditorPhase } from 'views/Administration/CampaignEditorPhaseContext';
import {
  getAiAssistedValueForNativeQuestion,
  getAiAssistedValueForQuestionInCustomGuestionGroup,
  isNativeQuestionAiEnabled,
  isQuestionPartOfCustomQuestionGroup,
} from 'views/AI/aiDraftQuestion';
import { useCampaignAIAssistedContext } from 'views/Performance/CampaignAIAssistedContextProvider';

interface Props {
  questionType?: string | null;
  aiAssisted?: boolean | null;
  shouldShowAIAssistedQuestionDescriptor?: ShouldShowAIAssistedQuestionDescriptorType | null;
  aiAssistedQuestionsVisibilityField?: string | null;
  questionName?: string | null;
}

const AIDraftsDescriptor: FC<PropsWithChildren<Props>> = ({
  children,
  aiAssistedQuestionsVisibilityField,
  aiAssisted,
  questionType,
  shouldShowAIAssistedQuestionDescriptor,
  // These props are needed for the rendering during the campaign phase in perf when people are
  // answering the questions.
  questionName,
}) => {
  const ref = React.createRef<HTMLSpanElement>();

  // This is used when rendering the questions in Campaign Editor.
  const phaseContext = useCampaignEditorPhase();

  // This is used when rendering the questions during the campaign phase
  // when answering them.
  const aIAssistedContext = useCampaignAIAssistedContext();

  if (!phaseContext && !aIAssistedContext) {
    return <>{children}</>;
  } else if (aIAssistedContext) {
    const aiAssistedQuestionsVisibilityFieldFromCurrentPhaseContext =
      getAIassistedQuestionsVisibilityFieldForCustomQuestionGroupName(
        aIAssistedContext?.phaseCustomQuestionGroupName
      );

    // Use fallback to default questions if the custom key is missing from the phase.
    const questionGroup = aIAssistedContext.currentPhase
      ? (aIAssistedContext.currentPhase[
          aIAssistedContext.phaseCustomQuestionGroupName
        ] ?? DEFAULT_OPEN_RESPONSE_QUESTIONS)
      : [];

    // This tells you if the current rendered question is part of the question group.
    const isQuestionPartOfCustomQuestionGroupBool =
      aIAssistedContext.currentPhase &&
      isQuestionPartOfCustomQuestionGroup(questionGroup, questionName ?? '');

    if (
      !aIAssistedContext.currentPhase ||
      !aiAssistedQuestionsVisibilityFieldFromCurrentPhaseContext ||
      // Check for custom questions.
      (!isQuestionPartOfCustomQuestionGroupBool &&
        // Check for "native" questions.
        !isNativeQuestionAiEnabled(questionName ?? '')) ||
      !aIAssistedContext.shouldShowAIAssistedQuestionDescriptor ||
      !aIAssistedContext.shouldShowAIAssistedQuestionDescriptor(
        questionType ?? '',
        ifUndefined(
          // Extract the relevant flag for the custom question from the group.
          getAiAssistedValueForQuestionInCustomGuestionGroup(
            questionGroup,
            questionName ?? ''
          ),
          // Or get it for the native question.
          getAiAssistedValueForNativeQuestion(
            questionName ?? '',
            aIAssistedContext?.currentPhase
          )
        ),
        aIAssistedContext.currentPhase[
          aiAssistedQuestionsVisibilityFieldFromCurrentPhaseContext
        ]
      )
    ) {
      return <>{children}</>;
    }
  } else if (phaseContext) {
    if (
      !phaseContext.enabledPhase ||
      !aiAssistedQuestionsVisibilityField ||
      !shouldShowAIAssistedQuestionDescriptor ||
      !shouldShowAIAssistedQuestionDescriptor(
        questionType ?? '',
        aiAssisted,
        phaseContext.enabledPhase[aiAssistedQuestionsVisibilityField]
      )
    ) {
      return <>{children}</>;
    }
  }

  return (
    <div className="d-lg-flex flex-row">
      {children}
      <span ref={ref} className="fw-normal ms-0 ms-md-3 text-nowrap">
        <i className="fe fe-zap text-primary small align-middle me-2" />
        <span className="text-primary small">
          <FormattedMessage
            id="app.views.widgets.inputs.ai_drafts_descriptor.ai_draft"
            defaultMessage="AI"
          />
        </span>
      </span>
      <UncontrolledPopover placement="top" trigger="hover" target={ref}>
        <FormattedMessage
          id="app.views.widgets.inputs.ai_drafts_descriptor.ai_draft.hover"
          defaultMessage="AI drafts are enabled for this question."
        />
      </UncontrolledPopover>
    </div>
  );
};

export default AIDraftsDescriptor;
